import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Chip, Container, LinearProgress, Stack, Typography, Paper, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { AbilityContext } from '../../components/Can';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import HallMaintenanceAssignmentsTable from './HallMaintenanceAssignmentsTable';
import HallMaintenanceAssignmentDialog from './HallMaintenanceAssignmentDialog';
import { HallMaintenanceTaskAssignment } from '../../domain/HallMaintenanceTaskAssignment';
import { useConfirm } from 'material-ui-confirm';
import moment from 'moment';

const HallMaintenanceAssignmentsPage = observer(() => {
    const { t } = useTranslation();
    const { hallMaintenanceStore, serverStore, publisherStore } = useStores();
    const ability = React.useContext(AbilityContext);
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { taskId } = useParams();
    const [selectedAssignment, setSelectedAssignment] = React.useState<HallMaintenanceTaskAssignment | undefined>();

    const numericTaskId = Number(taskId);
    const canManage = ability.can('manage', 'HallMaintenance');

    React.useEffect(() => {
        if (!hallMaintenanceStore.tasks.length) {
            hallMaintenanceStore.getTasks();
        }
    }, [hallMaintenanceStore]);

    React.useEffect(() => {
        if (numericTaskId) {
            hallMaintenanceStore.getAssignments(numericTaskId);
        }
    }, [hallMaintenanceStore, numericTaskId]);

    React.useEffect(() => {
        if (publisherStore.publishers.length === 0) {
            publisherStore.getAllPublishers();
        }
    }, [publisherStore]);

    const task: HallMaintenanceTask | undefined = hallMaintenanceStore.tasks.find(item => item.id === numericTaskId);
    const assignments = hallMaintenanceStore.assignmentsByTask[numericTaskId] || [];

    const handleCreate = () => {
        setSelectedAssignment(undefined);
        openDialog("manage-hall-maintenance-assignment");
    };

    const handleEdit = (assignment: HallMaintenanceTaskAssignment) => {
        setSelectedAssignment(assignment);
        openDialog("manage-hall-maintenance-assignment");
    };

    const handleDelete = (assignment: HallMaintenanceTaskAssignment) => {
        confirm({
            title: t("are_you_sure"),
            description: t("this_will_permanently_delete"),
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            hallMaintenanceStore.deleteAssignment(assignment.id, numericTaskId, closeDialog);
        });
    };

    const handleCloseDialog = () => {
        setSelectedAssignment(undefined);
        closeDialog();
    };

    const handleSubmit = (payload: Partial<HallMaintenanceTaskAssignment>, assignmentId?: number) => {
        if (assignmentId) {
            hallMaintenanceStore.updateAssignment(assignmentId, numericTaskId, payload, handleCloseDialog);
        } else {
            hallMaintenanceStore.createAssignment(payload, handleCloseDialog);
        }
    };

    const headerItems = (
        <Stack direction="row" spacing={1}>
            <Button color="inherit" onClick={() => navigate('/hall-maintenance')}>
                {t("back_to_tasks")}
            </Button>
            {canManage && (
                <Button color="inherit" onClick={handleCreate}>
                    {t("add_assignment")}
                </Button>
            )}
        </Stack>
    );

    return (
        <Panel title={t("task_assignments")} headerItems={headerItems}>
            <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
                {serverStore.serverRequestLoading && <LinearProgress sx={{ mb: 2 }} />}
                {!task && hallMaintenanceStore.tasks.length > 0 && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                            {t("task_not_found")}
                        </Typography>
                        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/hall-maintenance')}>
                            {t("back_to_tasks")}
                        </Button>
                    </Paper>
                )}
                {task && (
                    <Stack spacing={2}>
                        <Paper sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                <Typography variant="h6">
                                    {task.cardNumber && `#${task.cardNumber} - `}
                                    {task.title}
                                </Typography>
                            
                            </Stack>
                            {task.subTitle && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {`${t(task.category)}`} <br/>
                                    {task.subTitle} <br/>
                                    {`${t("frequency")}: ${task.frequencyLabel || `${task.frequencyInterval} ${t(task.frequencyUnit)}`}`}
                                </Typography>
                            )}
                        </Paper>

                        <Divider />

                        <HallMaintenanceAssignmentsTable
                            assignments={assignments}
                            canManage={canManage}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    </Stack>
                )}
            </Container>

            {task && (
                <HallMaintenanceAssignmentDialog
                    isOpen={isOpen("manage-hall-maintenance-assignment")}
                    onClose={handleCloseDialog}
                    onSubmit={handleSubmit}
                    task={task}
                    assignment={selectedAssignment}
                />
            )}
        </Panel>
    );
});

export default HallMaintenanceAssignmentsPage;
